import cloneDeep from 'lodash/cloneDeep';
import noop from 'lodash/noop';
import { proxy, subscribe, useSnapshot } from 'valtio';
import { subscribeKey, proxyMap, proxySet, derive } from 'valtio/utils';

import { storageCreator, StorageOptions } from './storage';

type SupportedTypes = 'bigint' | 'boolean' | 'number' | 'string' | 'undefined' | 'Array' | 'Object' | 'Map' | 'Set' | 'Date' | 'null';

function unsubscribeList(list: (() => void)[]) {
  while (list.length > 0) {
    const unsubscriber = list.shift();

    unsubscriber?.();
  }
}

function subscribeList<T extends Record<string, unknown>>(
  list: (() => void)[],
  subscribedKey: [string, SupportedTypes][],
  persistKeys: string[] | true,
  valtio: T,
  storage: ReturnType<typeof storageCreator>,
  immediate?: boolean,
  onUpdate?: (key: keyof T, value: T[keyof T]) => void,
) {
  if (list.length) {
    unsubscribeList(list);
  }

  list.push(
    ...subscribedKey.map(([key, type]) => {
      if (persistKeys !== true && !persistKeys.includes(key)) {
        return noop;
      }

      switch (type) {
        case 'Array':
        case 'Map':
        case 'Object':
        case 'Set': {
          const unsubs: (() => void)[] = [];

          unsubs.push(
            // Subscribe for object internal change
            subscribe(valtio[key] as object, () => {
              storage.set(key, valtio[key]);

              onUpdate?.(key, valtio[key] as T[keyof T]);
            }),
          );

          unsubs.push(
            // Subscribe for entire object change
            subscribeKey(valtio, key, (value) => {
              storage.set(key, value);

              onUpdate?.(key, value);
            }),
          );

          if (immediate) {
            storage.set(key, valtio[key]);
          }

          return () => {
            while (unsubs.length > 0) {
              const unsub = unsubs.shift();

              unsub?.();
            }
          };
        }
        case 'Date': // Exception with date, setting via Date.setHours, Date.setDate, etc wont trigger saving
        default:
          // Subscribe
          if (immediate) {
            storage.set(key, valtio[key]);
          }

          return subscribeKey(valtio, key, (value) => {
            storage.set(key, value);

            onUpdate?.(key, value);
          });
      }
    }),
  );
}

function getSerializer(type: SupportedTypes, serializer?: typeof JSON.stringify) {
  if (serializer) {
    return serializer;
  }

  switch (type) {
    case 'bigint':
      return function toString(num: bigint) {
        return JSON.stringify(String(num));
      };
    case 'number':
      return function toString(num: number) {
        if (Number.isNaN(num)) {
          return JSON.stringify('NaN');
        }

        return JSON.stringify(num);
      };
    case 'Map':
      return function toObject(map: Map<string, unknown>) {
        const obj: Record<string, unknown> = {};

        for (const [key, value] of map) {
          obj[key] = value;
        }

        return JSON.stringify(obj);
      };
    case 'Set':
      return function toArray(set: Set<unknown>) {
        return JSON.stringify([...set]);
      };
    case 'Date':
      return function toDateString(date: Date) {
        return String(+date);
      };
    default:
      return JSON.stringify;
  }
}

function getParser(type: SupportedTypes, parser?: typeof JSON.parse) {
  if (parser) {
    return parser;
  }

  switch (type) {
    case 'bigint':
      return function toBigInt(str: string) {
        return BigInt(JSON.parse(str));
      };
    case 'number':
      return function toNumber(str: string) {
        return Number(JSON.parse(str));
      };
    case 'Date':
      return function toDate(str: string) {
        return new Date(Number(str));
      };
    case 'Map':
      return function toMap(str: string) {
        return new Map<string | number, unknown>(
          Object.entries(JSON.parse(str)).map(([key, value]) => {
            // Try to convert back the key from string to number,
            // Otherwise, the getter will fail to retrieve after persisting
            // Since persisting will convert the number into string
            if (!Number.isNaN(Number(key))) {
              return [Number(key), value];
            }

            return [key, value];
          }),
        );
      };
    case 'Set':
      return function toSet(str: string) {
        return new Set(JSON.parse(str));
      };
    default:
      return JSON.parse;
  }
}

function getTypes(object: Record<string, unknown>) {
  const subscribedKey: [string, SupportedTypes][] = [];

  Object.getOwnPropertyNames(object).forEach((key) => {
    if (typeof object[key] === 'function') {
      return;
    }

    switch (typeof object[key]) {
      case 'object':
        switch (true) {
          case object[key] instanceof Map:
            subscribedKey.push([key, 'Map']);
            break;
          case object[key] instanceof Set:
            subscribedKey.push([key, 'Set']);
            break;
          case object[key] instanceof Date:
            subscribedKey.push([key, 'Date']);
            break;
          case Array.isArray(object[key]):
            subscribedKey.push([key, 'Array']);
            break;
          case object[key] === null:
            /* istanbul ignore next */
            if (process.env.NODE_ENV === 'development') {
              console.warn('Avoid using null value used in persisted data.');
              // eslint-disable-next-line no-console
              console.groupCollapsed();
              console.warn('- To use null in Date, use "new Date(NaN)" instead.');
              console.warn('- To use null in Number, use "NaN" instead.');
              console.warn('- To use null in String, use "" (empty string) instead.');
              console.warn('- To use null in Object, use "{}" (empty object) instead.');
              // eslint-disable-next-line no-console
              console.groupEnd();
            }

            subscribedKey.push([key, 'null']);
            break;
          default:
            // treat like a plain object
            subscribedKey.push([key, 'Object']);
            break;
        }

        break;
      case 'bigint':
      case 'boolean':
      case 'number':
      case 'string':
      case 'undefined':
      default:
        subscribedKey.push([key, typeof object[key] as SupportedTypes]);
        break;
    }
  });

  return subscribedKey;
}

/**
 * @abstract      Use localstorage to store and persist the data.
 *                I were to use Proxy to intercept, but blocked by this issue // https://github.com/pmndrs/valtio/issues/275
 * @param object  The object that will be used to pass on the valtio's proxy
 *                Each of the object's key will have their own storage path and may have their own
 *                Serializer & parser (see options).
 *                Supported object types:
 *                - Primitives
 *                - Array
 *                - Object
 *                - Map
 *                - Set
 *                - Date
 * @param key     A unique string to store the data into localstorage
 * @param options     Optional
 * @param  serializer Optional A function that will accept a value and converts it into string to be stored
 * @param  parser     Optional A function that will accept a string and converts it into whatever the original type was
 * @param  expiry     Optional Persist expiry time in milliseconds
 * @param  persist    Optional Keys of the @param object that will be persisted.
 */
function withPersist<T extends Record<string, unknown>>(
  object: T,
  key: string,
  options?: Pick<StorageOptions<T>, 'parser' | 'serializer' | 'expiry' | 'session'> & {
    readonly persist?: (keyof T)[];
    readonly onUpdate?: <U extends keyof T>(k: U, value: T[U]) => void;
  },
) {
  const subscriptions: (() => void)[] = [];
  const _object = cloneDeep(object);
  const subscribedKey = getTypes(object);
  const persistKeys = options?.persist ?? true;
  const storage = storageCreator<T>(key, {
    ...options,
    serializer: {
      ...subscribedKey.reduce((sum, conf) => {
        const _key = conf[0];

        return {
          ...sum,
          [_key]: getSerializer(conf[1], options?.serializer?.[_key]),
        };
      }, {}),
    },
    parser: {
      ...subscribedKey.reduce((sum, conf) => {
        const _key = conf[0];

        return {
          ...sum,
          [_key]: getParser(conf[1], options?.parser?.[_key]),
        };
      }, {}),
    },
    initialValue: cloneDeep(object),
  });

  // Fill objects with the value we get from localStorage first
  subscribedKey.forEach((conf) => {
    const _key = conf[0];
    const type = conf[1];
    const persisted = storage.get(_key as Exclude<keyof T, number | symbol>);

    switch (type) {
      case 'Map':
        _object[_key as keyof T] = proxyMap(persisted as Map<string, unknown>) as T[keyof T];
        break;
      case 'Set':
        _object[_key as keyof T] = proxySet(persisted as Set<unknown>) as T[keyof T];
        break;
      default:
        _object[_key as keyof T] = persisted as T[keyof T];
        break;
    }
  });

  const valtio = proxy(_object);

  subscribeList(subscriptions, subscribedKey, persistKeys as string[], valtio, storage, false, options?.onUpdate);

  return {
    proxy: valtio,
    storage,
    subscribedKey,
    resubscribe() {
      subscribeList(subscriptions, subscribedKey, persistKeys as string[], valtio, storage, true, options?.onUpdate);
    },
    unsubscribe() {
      unsubscribeList(subscriptions);
    },
  };
}

export { withPersist, proxyMap, proxySet, proxy, derive, subscribeKey, useSnapshot };
